import { Box, Grid } from "@mui/material";
import { LanguageContext } from "../../../../context/LanguageContext";
import { useContext, useRef, useState } from "react";
import { TitleAndActionBox } from "../../../common";
import { useNavigate } from "react-router-dom";
import { HeadingText, ParagraphText, UBButton } from "../../../../Libraries";
import formStyles from "../../SaleExpense/SaleExpenseForm/styles.module.css";
import styles from "./LoadMoney.module.css";
import {
  CopyIcon,
  EasypaisaIcon,
  JazzcashIcon,
  RupinBankIcon,
} from "../../../../assets/icons/icon";
import { analytics, triggerLogEvent } from "../../../../config/firebase";
import { useWalletContext } from "../../../../context/WalletContext";
import html2canvas from "html2canvas";

const COPYSTATUS = {
  accountTitle: "accountTitle",
  accountNumber: "accountNumber",
  iban: "iban",
};

const LoadMoney = () => {
  const [I18n] = useContext(LanguageContext);
  const navigate = useNavigate();
  const [copyStatus, setCopyStatus] = useState("");
  const [selectedBank, setSelectedBank] = useState("Bank");
  const qrCodeRef = useRef(null);
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);

  const { walletData } = useWalletContext();
  const raastDetail = walletData?.account_info?.raast_info?.raast;
  const easypaisaService = walletData?.cashin_services?.find((s) => s.name === "easypaisa");
  const consumerId = easypaisaService?.options?.find((o) => o.name === "collection")?.consumer_num || "";

  const BankSteps = {
    Bank: {
      steps: [
        { title: I18n.open_your_bank_app_easypaisa_or_jazzcash },
        { title: I18n.tap_send_or_transfer },
        { title: I18n.search_rupin },
        { title: I18n.enter_rupins_account_number_or_iban },
      ],
      icon: <RupinBankIcon />,
      label: I18n.bank,
    },
    Easypaisa: {
      groups: [
        {
          title: I18n.easypaisa_app_send_money,
          steps: [
            { title: I18n.open_easypaisa_app },
            { title: I18n.tap_on_send_money },
            { title: I18n.select_bank_transfer },
            { title: I18n.select_bank_rupin },
            { title: I18n.enter_account_number, account_number: raastDetail?.raast_details?.account_number, isCopy: true },
          ],
        },
        {
          title: I18n.easypaisa_app_collection,
          steps: [
            { title: I18n.open_easypaisa_app },
            { title: I18n.tap_on_see_all },
            { title: I18n.tap_on_fee_collection },
            { title: I18n.search_and_select_udhaar_app },
            { title: I18n.enter_consumer_id, account_number: consumerId, isCopy: true },
          ],
        },
      ],
      icon: <EasypaisaIcon />,
      label: I18n.easypaisa_tab,
    },
    Jazzcash: {
      steps: [
        { title: I18n.open_jazzcash_app },
        { title: I18n.tap_on_money_transfer },
        { title: I18n.tap_on_bank_transfer },
        { title: I18n.select_bank_rupin },
        {
          title: I18n.enter_account_number,
          account_number: raastDetail?.raast_details?.account_number,
          isCopy: true,
        },
      ],
      icon: <JazzcashIcon />,
      label: I18n.jazzcash_tab,
    },
  };

  const bankOptions = Object.keys(BankSteps);

  const handleDownloadQrCode = async () => {
    setIsDownloadLoading(true);
    triggerLogEvent(analytics, "w_load_download_qr");
    try {
      const svgEl = qrCodeRef.current?.querySelector("svg");
      if (!svgEl) throw new Error("svg not found");

      const clonedSvg = svgEl.cloneNode(true);

      const origW =
        parseFloat(svgEl.getAttribute("width")) ||
        svgEl.getBoundingClientRect().width ||
        190;
      const origH =
        parseFloat(svgEl.getAttribute("height")) ||
        svgEl.getBoundingClientRect().height ||
        190;
      if (!clonedSvg.getAttribute("viewBox")) {
        clonedSvg.setAttribute("viewBox", `0 0 ${origW} ${origH}`);
      }

      const QUIET_ZONE = 20;
      const QR_SIZE = 560;
      const canvasSize = QR_SIZE + QUIET_ZONE * 2; // 600 px total

      clonedSvg.setAttribute("width", QR_SIZE);
      clonedSvg.setAttribute("height", QR_SIZE);

      const imageEl = clonedSvg.querySelector("image");
      if (imageEl) {
        const href = imageEl.getAttribute("href");
        if (href && !href.startsWith("data:")) {
          try {
            const res = await fetch(href);
            const blob = await res.blob();
            const dataUri = await new Promise((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.readAsDataURL(blob);
            });
            imageEl.setAttribute("href", dataUri);
          } catch {
            // logo fetch blocked — proceed without inlining it
          }
        }
      }

      const svgData = new XMLSerializer().serializeToString(clonedSvg);
      const svgBlob = new Blob([svgData], {
        type: "image/svg+xml;charset=utf-8",
      });
      const url = URL.createObjectURL(svgBlob);

      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = canvasSize;
        canvas.height = canvasSize;
        const ctx = canvas.getContext("2d");

        // Disable canvas-level resampling so the browser does not blur
        // pixels when blitting the rasterised SVG onto the canvas.
        ctx.imageSmoothingEnabled = false;

        // White background covers the full canvas including the quiet zone.
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvasSize, canvasSize);

        // Draw the QR inset by QUIET_ZONE on every side.
        ctx.drawImage(img, QUIET_ZONE, QUIET_ZONE, QR_SIZE, QR_SIZE);

        const link = document.createElement("a");
        link.href = canvas.toDataURL("image/png");
        link.download = "qr_code.png";
        link.click();
        URL.revokeObjectURL(url);
        setIsDownloadLoading(false);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        setIsDownloadLoading(false);
      };
      img.src = url;
    } catch {
      html2canvas(qrCodeRef.current)
        .then((canvas) => {
          const link = document.createElement("a");
          link.href = canvas.toDataURL("image/png");
          link.download = "qr_code.png";
          link.click();
        })
        .catch(() => {})
        .finally(() => setIsDownloadLoading(false));
    }
  };

  const handlePrintQrCode = () => {
    triggerLogEvent(analytics, "w_load_print_qr");
    const printWindow = window.open("", "", "width=600,height=600");
    const qrCodeElement = qrCodeRef.current;

    printWindow.document.write(
      "<html><head><title>Print QR Code</title></head><body>",
    );
    printWindow.document.write('<div style="text-align: center;">');
    printWindow.document.write(qrCodeElement.outerHTML);
    printWindow.document.write("</div>");
    printWindow.document.write("</body></html>");
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  const handleCopyClick = async (textToCopy, key) => {
    try {
      await navigator.clipboard.writeText(textToCopy);
      triggerLogEvent(analytics, "w_load_copy_detail", { type: key });
      setCopyStatus(key);
      setTimeout(() => setCopyStatus(""), 1500);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  const handleShareDetails = () => {
    triggerLogEvent(analytics, "w_load_share");
    const phoneNumber = "";
    const message = encodeURIComponent(`Bank: ${raastDetail?.bank_name || ""}
Account title: ${raastDetail?.raast_details?.name}
Account number: ${raastDetail?.raast_details?.account_number}
IBAN: ${raastDetail?.raast_details?.iban}`);
    const whatsappUrl = `https://web.whatsapp.com/send?phone=${phoneNumber}&text=${message}`;
    window.open(whatsappUrl, "_blank");
  };

  const qrBtnClassName = `${styles.qrBtnStyle} textUppercase fontSize12`;

  return (
    <Grid
      container
      className={`${formStyles.root} ${formStyles.SaleExpenseFormWrapper} ${styles.loadMoneyWrapper}`}
    >
      <Grid item md={12}>
        <TitleAndActionBox
          data={{ title: I18n.add_money, isBack: true, onBack: () => navigate(-1) }}
        />

        <Box className={formStyles.formBodyGrid}>
          <Box className={formStyles.formColumn}>
            <Box className={`${formStyles.formBody} expenseFormBody ${styles.loadMoneyFormBody}`}>
              <HeadingText as="h1" className={styles.accountDetailsTitle}>
                {I18n.account_details}
              </HeadingText>

              <Box className={styles.accountDetailWrapper}>
                <Box className={styles.detailRow}>
                  <Box>
                    <ParagraphText className={"newLightColor fontSize14"}>
                      {I18n.account_title}
                    </ParagraphText>
                    <ParagraphText className={"newFontColor fontSize14"}>
                      {raastDetail?.raast_details?.name}
                    </ParagraphText>
                  </Box>
                </Box>

                <Box className={styles.detailRow}>
                  <Box>
                    <ParagraphText className={"newLightColor fontSize14"}>
                      {I18n.bank_name}
                    </ParagraphText>
                    <ParagraphText className={`newFontColor fontSize14 ${styles.bankNameStyle}`}>
                      {raastDetail?.bank_name || ""}{" "}
                    </ParagraphText>
                  </Box>
                </Box>

                <Box className={styles.detailRow}>
                  <ParagraphText className={"newLightColor fontSize14"}>
                    {I18n.account_number}
                  </ParagraphText>
                  <Box className={styles.valueWithCopy}>
                    <ParagraphText className={"newFontColor fontSize14"}>
                      {raastDetail?.raast_details?.account_number}
                    </ParagraphText>
                    <span
                      id="load_money_copy_account_number_btn"
                      className={styles.copyIconWrapper}
                      onClick={() => handleCopyClick(raastDetail?.raast_details?.account_number, COPYSTATUS.accountNumber)}
                    >
                      <CopyIcon width={14} height={14} />
                      {copyStatus === COPYSTATUS.accountNumber ? I18n.copied : ""}
                    </span>
                  </Box>
                </Box>

                <Box className={styles.detailRow}>
                  <ParagraphText className={"newLightColor fontSize14"}>
                    {I18n.IBAN}
                  </ParagraphText>
                  <Box className={styles.valueWithCopy}>
                    <ParagraphText className={"newFontColor fontSize14"}>
                      {raastDetail?.raast_details?.iban}
                    </ParagraphText>
                    <span
                      id="load_money_copy_iban_btn"
                      className={styles.copyIconWrapper}
                      onClick={() => handleCopyClick(raastDetail?.raast_details?.iban, COPYSTATUS.iban)}
                    >
                      <CopyIcon width={14} height={14} />
                      {copyStatus === COPYSTATUS.iban ? I18n.copied : ""}
                    </span>
                  </Box>
                </Box>
              </Box>

              <Box className={styles.accountDetailWrapper}>
                <ParagraphText className={`fontSize14 fontWeight500 newFontColor ${styles.instructionsTitle}`}>
                  {I18n.instruction_for_adding_money}
                </ParagraphText>

                <Box className={styles.bankTabContainer}>
                  {bankOptions.map((bank) => (
                    <button
                      id={`load_money_bank_tab_${bank.toLowerCase()}`}
                      key={bank}
                      className={`${styles.bankTab} ${selectedBank === bank ? styles.bankTabActive : ""}`}
                      onClick={() => setSelectedBank(bank)}
                    >
                      <span className={styles.bankTabIcon}>{BankSteps[bank].icon}</span>
                      <span className={styles.bankTabLabel}>{BankSteps[bank].label || bank}</span>
                    </button>
                  ))}
                </Box>

                {BankSteps[selectedBank].groups
                  ? BankSteps[selectedBank].groups.map((group, gi) => (
                      <Box key={gi} className={styles.stepsList} style={{ marginTop: gi > 0 ? 32 : 0 }}>
                        <ParagraphText className="fontSize14 fontWeight500 newFontColor" style={{ marginBottom: 8 }}>
                          {group.title}
                        </ParagraphText>
                        {group.steps.map((step, index) => (
                          <Box key={index} className={styles.stepItem}>
                            <span className={styles.stepNumber}>{index + 1}.</span>
                            <ParagraphText className={`fontSize14 newFontColor ${styles.stepText}`}>
                              {step.title}
                              {step.isCopy && step.account_number && (
                                <span
                                  id={`load_money_step_copy_btn_${gi}_${index}`}
                                  className={styles.stepCopyInline}
                                  onClick={() =>
                                    handleCopyClick(
                                      step.account_number,
                                      `account_number_${gi}_${index}`,
                                    )
                                  }
                                >
                                  {step.account_number}
                                  <CopyIcon className={styles.stepCopyIcon} />
                                  {copyStatus === `account_number_${gi}_${index}`
                                    ? ` ${I18n.copied}`
                                    : ""}
                                </span>
                              )}
                            </ParagraphText>
                          </Box>
                        ))}
                      </Box>
                    ))
                  : (
                    <Box className={styles.stepsList}>
                      {BankSteps[selectedBank].steps.map((step, index) => (
                        <Box key={index} className={styles.stepItem}>
                          <span className={styles.stepNumber}>{index + 1}.</span>
                          <ParagraphText className={`fontSize14 newFontColor ${styles.stepText}`}>
                            {step.title}
                            {step.isCopy && step.account_number && (
                              <span
                                id={`load_money_step_copy_btn_${index}`}
                                className={styles.stepCopyInline}
                                onClick={() =>
                                  handleCopyClick(
                                    step.account_number,
                                    `account_number_${index}`,
                                  )
                                }
                              >
                                {step.account_number}
                                <CopyIcon className={styles.stepCopyIcon} />
                                {copyStatus === `account_number_${index}`
                                  ? ` ${I18n.copied}`
                                  : ""}
                              </span>
                            )}
                          </ParagraphText>
                        </Box>
                      ))}
                    </Box>
                  )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default LoadMoney;
